import type { TDocumentDefinitions, Content, TableCell } from 'pdfmake/interfaces';
import {
  mm, fmtPrice, fmtDate, interpolate,
  companyLines, companySocialsLine, partyLines, logoBlock, totalsRows, buildVars,
  computeVatBreakdown,
  COLUMN_LABELS, COLUMN_WIDTHS, linesCell,
  type DesignCtx,
} from './design-utils';
import { amountInWordsFr } from '../amount-in-words';

// "Minimal" — very light, thin rules, black-on-white. No fills, no bands.
// Title aligned left, everything breathes.
export function buildMinimalDoc(ctx: DesignCtx): TDocumentDefinitions {
  const { options: o, data, company, docTypeTitle } = ctx;
  const vars = buildVars(ctx);
  const title = o.customTitle || docTypeTitle;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const headerCols: any[] = [];
  const logo = logoBlock(ctx);
  const socialsLine = companySocialsLine(company, o);
  const companyStack: { stack: Content[] } = {
    stack: [
      ...(o.showCompanyName ? [{ text: company.companyName, fontSize: 11, bold: true, color: '#222' } as Content] : []),
      ...companyLines(company, o).map((line) => ({ text: line, fontSize: 9, color: '#222' } as Content)),
      ...(socialsLine ? [{ text: socialsLine, fontSize: 8, color: '#777', margin: [0, 2, 0, 0] } as Content] : []),
    ],
  };
  const titleStack: { stack: Content[]; alignment: 'right' } = {
    stack: [
      ...(o.showTitle ? [{ text: title.toUpperCase(), fontSize: 22, color: '#111', characterSpacing: 2 } as Content] : []),
      ...(o.showNumber ? [{ text: data.number, fontSize: 10, color: '#777', margin: [0, 2, 0, 0] } as Content] : []),
    ],
    alignment: 'right',
  };

  if (o.logoPosition === 'left' && logo) {
    headerCols.push({ width: mm(o.logoSizeMm + 5), stack: [logo] });
    headerCols.push({ width: '*', ...companyStack });
    headerCols.push({ width: 'auto', ...titleStack });
  } else if (o.logoPosition === 'right' && logo) {
    headerCols.push({ width: '*', ...companyStack });
    headerCols.push({ width: 'auto', ...titleStack });
    headerCols.push({ width: mm(o.logoSizeMm + 5), stack: [logo], alignment: 'right' });
  } else {
    headerCols.push({ width: '*', ...companyStack });
    headerCols.push({ width: 'auto', ...titleStack });
  }

  const thinRule: Content = {
    canvas: [{ type: 'line', x1: 0, y1: 0, x2: mm(180), y2: 0, lineWidth: 0.5, lineColor: '#CCC' }],
    margin: [0, 12, 0, 12],
  };

  const partyLabel = data.supplier ? 'Fournisseur' : 'Client';
  const partyTxt = partyLines(data.supplier ?? data.client, o);
  const infoRow: Content = {
    columns: [
      {
        width: '*',
        stack: [
          ...(o.showDate ? [{ text: `Date : ${fmtDate(data.docDate)}`, fontSize: 9 } as Content] : []),
          ...(o.showValidityDate && data.validityDate ? [{ text: `Validité : ${fmtDate(data.validityDate)}`, fontSize: 9 } as Content] : []),
        ],
      },
      {
        width: '*',
        stack: [
          { text: partyLabel, fontSize: 9, color: '#999' },
          ...partyTxt.map((line, i) => ({ text: line, fontSize: i === 0 ? 11 : 9, bold: i === 0 })),
        ],
      },
    ],
    margin: [0, 0, 0, 15],
  };

  const header: TableCell[] = o.linesColumns.map((c) => ({
    text: COLUMN_LABELS[c], fontSize: 8, color: '#999',
    alignment: c === 'description' ? 'left' : c === 'number' ? 'center' : 'right',
    border: [false, false, false, true],
  }));
  const rows: TableCell[][] = data.lines.map((l) => o.linesColumns.map((c) => {
    const cell = linesCell(c, l);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const obj: any = typeof cell === 'object' && cell !== null ? cell : { text: String(cell) };
    return { ...obj, border: [false, false, false, false] } as TableCell;
  }));

  const linesTable: Content = {
    margin: [0, 0, 0, 15],
    table: {
      headerRows: 1,
      widths: o.linesColumns.map((c) => COLUMN_WIDTHS[c]),
      body: [header, ...rows],
    },
    layout: {
      hLineWidth: (i: number) => i === 1 ? 0.5 : 0,
      vLineWidth: () => 0,
      hLineColor: () => '#DDD',
      paddingLeft: () => 0, paddingRight: () => 6, paddingTop: () => 4, paddingBottom: () => 4,
    },
  };

  // VAT breakdown (grouped by rate) — minimal style, no fills, thin rules only
  const vatRows = o.showVatBreakdown ? computeVatBreakdown(data.lines) : [];
  const vatHeader: TableCell[] = [
    { text: 'Base TVA', fontSize: 8, color: '#999', alignment: 'left', border: [false, false, false, true] },
    { text: 'Taux TVA', fontSize: 8, color: '#999', alignment: 'center', border: [false, false, false, true] },
    { text: 'TVA', fontSize: 8, color: '#999', alignment: 'right', border: [false, false, false, true] },
  ];
  const vatBodyRows: TableCell[][] = vatRows.map((r) => [
    { text: fmtPrice(r.base), alignment: 'left', fontSize: 9, border: [false, false, false, false] },
    { text: `${r.rate}%`, alignment: 'center', fontSize: 9, border: [false, false, false, false] },
    { text: fmtPrice(r.tax), alignment: 'right', fontSize: 9, border: [false, false, false, false] },
  ]);
  const vatBreakdownTable: Content = (o.showVatBreakdown && vatRows.length > 0) ? {
    table: { widths: ['*', 'auto', 'auto'], body: [vatHeader, ...vatBodyRows] },
    layout: {
      hLineWidth: (i: number) => i === 1 ? 0.5 : 0, vLineWidth: () => 0,
      hLineColor: () => '#DDD',
      paddingLeft: () => 0, paddingRight: () => 6, paddingTop: () => 4, paddingBottom: () => 4,
    },
  } : { text: '' };

  const totals = totalsRows(ctx);
  const totalsBodyTable: Content = totals.length ? {
    table: { widths: ['*', 'auto'], body: totals },
    layout: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? 0.5 : 0,
      vLineWidth: () => 0,
      hLineColor: () => '#DDD',
      paddingTop: () => 4, paddingBottom: () => 4, paddingLeft: () => 4, paddingRight: () => 0,
    },
  } : { text: '' };

  const totalsTable: Content = (totals.length || vatRows.length) ? {
    margin: [0, 0, 0, 15],
    columns: [
      vatRows.length > 0 ? { width: mm(80), stack: [vatBreakdownTable] } : { width: '*', text: '' },
      { width: 10, text: '' },
      totals.length > 0 ? { width: mm(80), stack: [totalsBodyTable] } : { width: '*', text: '' },
    ],
  } : { text: '' };

  const amountLabel = (() => {
    const docLabel = (o.customTitle || docTypeTitle).toLowerCase();
    if (docLabel.includes('devis')) return 'Arrêter le Présent Devis à la Somme de :';
    if (docLabel.includes('livraison')) return 'Arrêter le Présent BL à la Somme de :';
    if (docLabel.includes('avoir')) return 'Arrêter le Présent Avoir à la Somme de :';
    if (docLabel.includes('réception') || docLabel.includes('reception')) return 'Arrêter le Présent BR à la Somme de :';
    if (docLabel.includes('commande')) return 'Arrêter la Présente Commande à la Somme de :';
    return 'Arrêter la Présente Facture à la Somme de :';
  })();
  const amountInWordsBlock: Content = o.showAmountInWords ? {
    stack: [
      { text: amountLabel, fontSize: 9, color: '#999', margin: [0, 0, 0, 2] },
      { text: amountInWordsFr(data.totalTtc), fontSize: 9, bold: true, color: '#222' },
    ],
    margin: [0, 0, 0, 15],
  } : { text: '' };

  const notesBlock: Content = (o.showNotes && data.notes) ? {
    stack: [
      { text: 'Notes', fontSize: 9, color: '#999', margin: [0, 0, 0, 4] },
      { text: data.notes, fontSize: 9, color: '#333' },
    ],
    margin: [0, 0, 0, 15],
  } : { text: '' };

  return {
    pageSize: 'A4',
    pageMargins: [mm(18), mm(18), mm(18), mm(22)],
    content: [
      { columns: headerCols },
      thinRule,
      infoRow,
      linesTable,
      totalsTable,
      amountInWordsBlock,
      notesBlock,
    ],
    footer: o.showFooter ? ((currentPage: number, pageCount: number): Content => ({
      margin: [mm(18), 8, mm(18), 0],
      columns: [
        { text: interpolate(o.footerText, vars), fontSize: 8, color: '#999', alignment: o.showPageNumbers ? 'left' : 'center' },
        ...(o.showPageNumbers ? [{ text: `${currentPage} / ${pageCount}`, fontSize: 8, color: '#999', alignment: 'right' as const }] : []),
      ],
    })) : undefined,
    defaultStyle: { fontSize: 9 },
  };
}
